import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/6197108/pexels-photo-6197108.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("creds"); // creds | mfa
  const [tempToken, setTempToken] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submitCreds = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email.trim().toLowerCase(), password);
      if (res.mfaRequired) {
        setTempToken(res.tempToken);
        setStep("mfa");
        toast.info("Enter your authenticator code");
      } else {
        toast.success("Welcome back");
        navigate("/");
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyMfa(tempToken, code);
      toast.success("Verified");
      navigate("/");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 vault-gradient text-white overflow-hidden">
        <div className="relative z-10">
          <div className="bg-white rounded-xl px-4 py-2.5 inline-flex">
            <img src="/iclean-logo.webp" alt="iClean" className="h-9 w-auto" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mt-3">Operations Platform</p>
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-4xl font-extrabold leading-tight">A spotless space, without the hassle.</h2>
        </div>
        <img src={HERO} alt="iClean team" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8">
            <img src="/iclean-logo.webp" alt="iClean" className="h-9 w-auto" />
          </div>

          {step === "creds" ? (
            <form onSubmit={submitCreds} data-testid="login-form">
              <h1 className="font-display text-2xl font-bold text-slate-900">Sign in</h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">Enter your credentials to continue.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@icleannw.co.uk" required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" data-testid="login-password-input" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5" />
                </div>
                <Button type="submit" disabled={busy} data-testid="login-submit-btn"
                  className="w-full bg-brand hover:bg-brand-dark h-11">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={submitMfa} data-testid="login-mfa-form">
              <h1 className="font-display text-2xl font-bold text-slate-900">Two-factor</h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">Enter the 6-digit code from your authenticator app.</p>
              <div className="flex justify-center mb-6">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="login-mfa-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button type="submit" disabled={busy || code.length < 6} data-testid="login-mfa-submit-btn"
                className="w-full bg-brand hover:bg-brand-dark h-11">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
